import { useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import Papa from 'papaparse';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  UploadIcon,
  AlertCircleIcon,
  XIcon,
  DownloadIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Navbar } from '@/components/ui/navbar';
import { Footer } from '@/components/ui/footer';
import { useFileUpload } from '@/hooks/useFileUpload';
import {
  importRidesFromCsv,
  type ImportRidesResult,
  type CreatedEvent,
} from '@/server/importRides';
import {
  parseCsvRow,
  type ParsedCsvRow,
  type CsvRowError,
} from '@/helpers/parseCsvRow';

export const Route = createFileRoute('/admin/import')({
  component: ImportPage,
  beforeLoad: ({ context, location }) => {
    if (!context.profile) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
    if (context.profile.level !== 'superadmin') {
      throw redirect({ to: '/' });
    }
  },
});

type ImportState =
  | { step: 'upload'; parseError?: string }
  | {
      step: 'preview';
      fileName: string;
      validRows: Array<{ rowIndex: number; data: ParsedCsvRow }>;
      skippedRows: CsvRowError[];
    }
  | { step: 'importing' }
  | { step: 'done'; result: ImportRidesResult };

/**
 * Admin page for importing group rides from a CSV file.
 * Parses CSV client-side, shows a preview summary, then
 * sends valid rows to the server for import.
 */
function ImportPage() {
  const [state, setState] = useState<ImportState>({ step: 'upload' });

  /** Parses a CSV file and transitions to the preview step. */
  function handleFile(file: File) {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          const messages = results.errors
            .slice(0, 3)
            .map((e) => e.message)
            .join('; ');
          setState({
            step: 'upload',
            parseError: `CSV parsing failed: ${messages}`,
          });
          return;
        }

        const validRows: Array<{ rowIndex: number; data: ParsedCsvRow }> = [];
        const skippedRows: CsvRowError[] = [];

        results.data.forEach((row, index) => {
          const parsed = parseCsvRow(row, index + 1);
          if (parsed.ok) {
            validRows.push({ rowIndex: index + 1, data: parsed.data });
          } else {
            skippedRows.push(parsed.error);
          }
        });

        setState({
          step: 'preview',
          fileName: file.name,
          validRows,
          skippedRows,
        });
      },
    });
  }

  /** Sends valid rows to the server function for import. */
  async function handleImport() {
    if (state.step !== 'preview') return;

    const { validRows } = state;
    setState({ step: 'importing' });

    try {
      const result = await importRidesFromCsv({
        data: { rows: validRows },
      });
      setState({ step: 'done', result });
    } catch (err) {
      setState({
        step: 'done',
        result: {
          created: [],
          skipped: [
            {
              row: 0,
              reason:
                err instanceof Error ? err.message : 'Unknown error',
            },
          ],
        },
      });
    }
  }

  /** Resets to the initial upload state. */
  function handleReset() {
    setState({ step: 'upload' });
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 container mx-auto px-6 py-12 max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 mb-8">
          Import Group Rides
        </h1>

        {state.step === 'upload' && (
          <div className="space-y-4">
            <CsvDropzone onFile={handleFile} />
            {state.parseError && (
              <div
                className="flex items-center gap-2 text-destructive text-sm"
                role="alert"
              >
                <AlertCircleIcon className="size-4 shrink-0" />
                <span>{state.parseError}</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadTemplate}
            >
              <DownloadIcon className="w-3.5 h-3.5" />
              Download template CSV
            </Button>
          </div>
        )}

        {state.step === 'preview' && (
          <PreviewSection
            fileName={state.fileName}
            validCount={state.validRows.length}
            skippedRows={state.skippedRows}
            onImport={handleImport}
            onReset={handleReset}
          />
        )}

        {state.step === 'importing' && <ImportingSection />}

        {state.step === 'done' && (
          <ResultsSection result={state.result} onReset={handleReset} />
        )}
      </main>
      <Footer />
    </div>
  );
}

const TEMPLATE_CSV_HEADERS = [
  'Organization',
  'Organization Link',
  'Event Name',
  'Date',
  'Location',
  'Description',
  'Event Type',
  'Discipline',
  'Website Link',
  'Start Time',
  'Distance (km) - 1',
  'GPX File Link',
  'Distance (km) - 2',
  'GPX File Link - 2',
  'Distance (km) - 3',
  'GPX File Link- 3',
].join(',');

/** Downloads a template CSV file with the expected column headers. */
function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV_HEADERS + '\n'], {
    type: 'text/csv',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ride-import-template.csv';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Drag-and-drop CSV file upload zone using the shared useFileUpload hook.
 */
function CsvDropzone({ onFile }: { onFile: (file: File) => void }) {
  const [{ isDragging, isInvalidType, errors }, actions] = useFileUpload({
    accept: 'text/csv,application/vnd.ms-excel,.csv',
    maxSize: 10 * 1024 * 1024,
    onFileSelect: (selected) => {
      onFile(selected.file);
    },
  });

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`relative flex min-h-40 flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed p-6 transition-colors has-[input:focus]:border-ring has-[input:focus]:ring-[3px] has-[input:focus]:ring-ring/50 ${
          isInvalidType
            ? 'border-destructive bg-destructive/10'
            : isDragging
              ? 'border-input bg-accent/50'
              : 'border-input'
        }`}
        onDragEnter={actions.handleDragEnter}
        onDragLeave={actions.handleDragLeave}
        onDragOver={actions.handleDragOver}
        onDrop={actions.handleDrop}
      >
        <input
          {...actions.getInputProps()}
          aria-label="Upload CSV file"
          className="sr-only"
        />
        {isInvalidType ? (
          <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
            <div
              aria-hidden="true"
              className="mb-2 flex size-11 shrink-0 items-center justify-center rounded-full border border-destructive/30 bg-destructive/10"
            >
              <AlertCircleIcon className="size-4 text-destructive" />
            </div>
            <p className="mb-1.5 font-medium text-sm text-destructive">
              Unsupported file type
            </p>
            <p className="text-destructive/80 text-xs">
              Only CSV files are accepted
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
            <div
              aria-hidden="true"
              className="mb-2 flex size-11 shrink-0 items-center justify-center rounded-full border bg-background"
            >
              <FileText className="size-4 opacity-60" />
            </div>
            <p className="mb-1.5 font-medium text-sm">
              Drop your CSV file here
            </p>
            <p className="text-muted-foreground text-xs">
              CSV file (max. 10MB)
            </p>
            <Button
              className="mt-4"
              onClick={actions.openFileDialog}
              variant="outline"
              type="button"
            >
              <UploadIcon
                aria-hidden="true"
                className="-ms-1 size-4 opacity-60"
              />
              Select file
            </Button>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div
          className="flex items-center gap-1 text-destructive text-xs"
          role="alert"
        >
          <AlertCircleIcon className="size-3 shrink-0" />
          <span>{errors[0]}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Preview section showing valid/skipped row counts and skip reasons.
 */
function PreviewSection({
  fileName,
  validCount,
  skippedRows,
  onImport,
  onReset,
}: {
  fileName: string;
  validCount: number;
  skippedRows: CsvRowError[];
  onImport: () => void;
  onReset: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-200">
        <FileText className="w-5 h-5 text-slate-500 shrink-0" />
        <span className="text-sm text-slate-700 truncate">{fileName}</span>
        <button
          aria-label="Remove file"
          className="ml-auto flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground"
          onClick={onReset}
          type="button"
        >
          <XIcon aria-hidden="true" className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-800">
              Valid
            </span>
          </div>
          <p className="text-2xl font-bold text-emerald-900">{validCount}</p>
        </div>
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              Skipped
            </span>
          </div>
          <p className="text-2xl font-bold text-amber-900">
            {skippedRows.length}
          </p>
        </div>
      </div>

      {skippedRows.length > 0 && (
        <SkippedRowsList rows={skippedRows} />
      )}

      <div className="flex gap-3">
        <Button onClick={onImport} disabled={validCount === 0}>
          Import {validCount} Ride{validCount !== 1 ? 's' : ''}
        </Button>
        <Button variant="outline" onClick={onReset}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Compact list of skipped rows with their reasons.
 */
function SkippedRowsList({ rows }: { rows: CsvRowError[] }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <p className="text-sm font-medium text-slate-700">Skip Reasons</p>
      </div>
      <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
        {rows.map((row, i) => (
          <li
            key={`skip-${row.row}-${i}`}
            className="px-4 py-2 text-sm flex items-start gap-2"
          >
            <span className="text-slate-400 shrink-0">Row {row.row}:</span>
            <span className="text-slate-600">{row.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Loading indicator shown while import is in progress. */
function ImportingSection() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12">
      <Spinner size="md" />
      <p className="text-sm text-slate-600">
        Importing rides... This may take a moment.
      </p>
    </div>
  );
}

/**
 * Results section showing created/skipped counts and a table of created events.
 */
function ResultsSection({
  result,
  onReset,
}: {
  result: ImportRidesResult;
  onReset: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-800">
              Created
            </span>
          </div>
          <p className="text-2xl font-bold text-emerald-900">
            {result.created.length}
          </p>
        </div>
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              Skipped
            </span>
          </div>
          <p className="text-2xl font-bold text-amber-900">
            {result.skipped.length}
          </p>
        </div>
      </div>

      {result.created.length > 0 && (
        <CreatedEventsTable events={result.created} />
      )}

      {result.skipped.length > 0 && (
        <SkippedRowsList rows={result.skipped} />
      )}

      <Button variant="outline" onClick={onReset}>
        Import Another File
      </Button>
    </div>
  );
}

/**
 * Table of successfully created events with links to their detail pages.
 */
function CreatedEventsTable({ events }: { events: CreatedEvent[] }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <p className="text-sm font-medium text-slate-700">
          Created Events
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              <th className="px-4 py-2 font-medium text-slate-500">Name</th>
              <th className="px-4 py-2 font-medium text-slate-500">Organizer</th>
              <th className="px-4 py-2 font-medium text-slate-500">Date</th>
              <th className="px-4 py-2 font-medium text-slate-500">Location</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {events.map((event) => (
              <tr key={event.id}>
                <td className="px-4 py-2">
                  <Link
                    to="/event/$eventId"
                    params={{ eventId: event.id }}
                    className="text-blue-600 hover:underline"
                  >
                    {event.name}
                  </Link>
                </td>
                <td className="px-4 py-2 text-slate-600">
                  {event.organizerName}
                </td>
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                  {event.startDate
                    ? new Date(event.startDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'TBD'}
                </td>
                <td className="px-4 py-2 text-slate-600 truncate max-w-48">
                  {event.location ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
